import { useRef, useState, type DragEvent, type KeyboardEvent } from 'react';

// Reusable drag-and-drop + click-to-pick file zone. Shared by the
// dashboard file-drop UI (#14) and the unauthed reverse-drop upload
// page (#15). Single-file selection. Keyboard accessible.
export default function FileDropZone({
  file,
  onPick,
  disabled,
}: {
  file: File | null;
  onPick: (f: File | null) => void;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  function open() {
    if (!disabled) inputRef.current?.click();
  }
  function onKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      open();
    }
  }
  function onDrop(e: DragEvent) {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    const f = e.dataTransfer.files?.[0] ?? null;
    if (f) onPick(f);
  }

  return (
    <div
      className={`dropzone${dragging ? ' dragging' : ''}`}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label="Choose a file or drag one here"
      onClick={open}
      onKeyDown={onKeyDown}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      <input
        ref={inputRef}
        type="file"
        style={{ display: 'none' }}
        disabled={disabled}
        onChange={(e) => onPick(e.target.files?.[0] ?? null)}
      />
      {file ? (
        <div>
          <div className="picked">{file.name}</div>
          <div className="muted" style={{ marginTop: 6 }}>
            {fmtBytes(file.size)} · click to replace
          </div>
        </div>
      ) : (
        <div>
          <div>drag a file here, or click to choose</div>
          <div className="muted" style={{ marginTop: 6 }}>
            single file · stored in memory · single-use
          </div>
        </div>
      )}
    </div>
  );
}

export function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KiB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MiB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GiB`;
}
